import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import type { Campaign } from '@/features/campaigns/types/campaign'
import { validGamificationPasses } from '@/features/passes'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { useGamificationData } from '@/shared/hooks/useGamificationData'

import {
  buildCampaignMissionLookup,
  buildPassDisplayItems,
  campaignCategories as buildCampaignCategories,
  categoriesForMission,
  DAILY_CATEGORY,
  isClaimable,
  missionToDisplayItem,
  PASS_CATEGORY,
  TODAS_CATEGORY,
  WEEKLY_CATEGORY,
  type MissionCategory,
  type MissionListItem
} from '../lib/missionCategories'
import { resolveMissionSubmission } from '../lib/submission'
import { useMissionsStore } from '../stores/useMissionsStore'
import type { MissionSubmission } from '../types/mission'

export function useMissionsCategoryData() {
  const { missions } = useMissionsStore()
  const gamificationData = useGamificationData()
  const { progress, passObjectiveProgresses, campaignIds } = useUserProgress()

  // Same query key GlobalPrefetch eagerly warms at Shell-mount — reads from
  // cache here, no extra network request in the common case.
  const { data: campaignsData } = useQuery<{ records: Campaign[] }>({
    queryKey: ['/user/campaigns']
  })
  const campaigns = campaignsData?.records ?? []

  const visibleMissionIds = useMemo(
    () => new Set(missions.map(mission => mission.id)),
    [missions]
  )

  const allCampaignCategories = useMemo(
    () => buildCampaignCategories(campaigns),
    [campaigns]
  )

  const campaignMissionLookup = useMemo(
    () =>
      buildCampaignMissionLookup(campaigns, allCampaignCategories, visibleMissionIds),
    [campaigns, allCampaignCategories, visibleMissionIds]
  )

  // Same priority resolution useMissionSubmission uses per-mission — grouped
  // here by mission_id so the listing can show the "Recusada" tag without
  // diverging from what the detail modal considers the relevant submission.
  const submissionByMissionId = useMemo(() => {
    const byMission = new Map<number, MissionSubmission[]>()
    for (const submission of progress?.mission_submissions ?? []) {
      const list = byMission.get(submission.mission_id) ?? []
      list.push(submission)
      byMission.set(submission.mission_id, list)
    }
    const result = new Map<number, MissionSubmission>()
    for (const [missionId, submissions] of byMission) {
      const resolved = resolveMissionSubmission(submissions)
      if (resolved) result.set(missionId, resolved)
    }
    return result
  }, [progress?.mission_submissions])

  const allMissionItems = useMemo(
    () =>
      missions.map(mission => {
        // categories[0] is the primary (campaign-first) home; the full list is
        // the set of filter chips the mission shows under — a campaign-linked
        // daily/weekly mission belongs to both its campaign and its recurrence.
        const cats = categoriesForMission(mission, campaignMissionLookup)
        return missionToDisplayItem(
          mission,
          cats[0],
          cats.map(category => category.id),
          submissionByMissionId.get(mission.id)
        )
      }),
    [missions, campaignMissionLookup, submissionByMissionId]
  )

  // Claimable items (reward earned, not collected) are pulled out into their
  // own pinned banner (MissionClaimBanner) instead of living in the regular
  // category-filtered list — category membership/counts below are computed
  // from what's left, so a chip never claims to hold a mission that's
  // actually surfaced in the banner instead.
  const claimableItems = useMemo(
    () => allMissionItems.filter(isClaimable),
    [allMissionItems]
  )
  const missionItems = useMemo(
    () => allMissionItems.filter(item => !isClaimable(item)),
    [allMissionItems]
  )

  const passItems = useMemo(
    () =>
      validGamificationPasses(
        gamificationData?.passes ?? [],
        campaignIds
      ).flatMap(pass =>
        buildPassDisplayItems(pass, passObjectiveProgresses)
      ),
    [gamificationData?.passes, campaignIds, passObjectiveProgresses]
  )

  const items: MissionListItem[] = useMemo(
    () => [...missionItems, ...passItems],
    [missionItems, passItems]
  )

  const campaignCategoriesWithMissions = useMemo(
    () =>
      allCampaignCategories.filter(category =>
        missionItems.some(item => item.category.id === category.id)
      ),
    [allCampaignCategories, missionItems]
  )

  // Chips de recorrência só entram quando têm conteúdo, como o de passe e os de
  // campanha já faziam. Sem isso, ocultar as missões diárias de um aluno por
  // regra de campanha deixaria a aba "Diárias" na tela, vazia — trocaria
  // conteúdo indesejado por uma aba morta, em vez de limpar o feed.
  //
  // A contagem lê `categoryIds` (todos os chips do item), não `category` (só o
  // primário): missão diária vinculada a campanha tem a campanha como primário e
  // continua pertencendo ao chip "Diárias".
  //
  // Contamos sobre `missionItems`, que ainda traz os itens resgatados — eles só
  // migram para "Resgatadas" dentro de groupItemsByCategory, na renderização.
  // Contar depois faria o chip sumir para quem concluiu tudo, que é justamente
  // quem não tem restrição nenhuma.
  const recurrenceCategoriesWithMissions = useMemo(
    () =>
      [DAILY_CATEGORY, WEEKLY_CATEGORY].filter(category =>
        missionItems.some(item => item.categoryIds.includes(category.id))
      ),
    [missionItems]
  )

  const categories: MissionCategory[] = useMemo(() => {
    const list: MissionCategory[] = [TODAS_CATEGORY, ...recurrenceCategoriesWithMissions]
    if (passItems.length > 0) list.push(PASS_CATEGORY)
    list.push(...campaignCategoriesWithMissions)
    return list
  }, [recurrenceCategoriesWithMissions, passItems.length, campaignCategoriesWithMissions])

  return { items, categories, claimableItems }
}
